import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useRuntimeHealthAccess } from "@/hooks/useRuntimeHealthAccess";
import { Plus, RefreshCw, Route, Trash2 } from "lucide-react";

type Rule = {
  id: string;
  label: string;
  release_pattern: string | null;
  max_p_value: number;
  min_rate_delta: number;
  min_release_errors: number;
  min_severity: "info" | "warn" | "error";
  channel: "email" | "slack";
  destination: string;
  escalate: boolean;
  enabled: boolean;
  created_at: string;
};

type Outcome = {
  id: string;
  created_at: string;
  rule_label: string | null;
  release: string;
  baseline_release: string | null;
  z_score: number | null;
  p_value: number | null;
  rate_delta: number | null;
  release_errors: number | null;
  channel: string | null;
  destination: string | null;
  outcome: "delivered" | "failed" | "skipped";
  skip_reason: string | null;
  error: string | null;
};

type Draft = {
  label: string;
  release_pattern: string;
  max_p_value: string;
  min_rate_delta: string;
  min_release_errors: string;
  min_severity: Rule["min_severity"];
  channel: Rule["channel"];
  destination: string;
  escalate: boolean;
};

const EMPTY: Draft = {
  label: "",
  release_pattern: "",
  max_p_value: "0.05",
  min_rate_delta: "0.02",
  min_release_errors: "5",
  min_severity: "warn",
  channel: "slack",
  destination: "",
  escalate: false,
};

export function FrontendErrorRegressionRoutingPanel() {
  const { toast } = useToast();
  const { canConfigure } = useRuntimeHealthAccess();
  const [rules, setRules] = useState<Rule[]>([]);
  const [outcomes, setOutcomes] = useState<Outcome[]>([]);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [{ data: r, error: rErr }, { data: o }] = await Promise.all([
      supabase
        .from("frontend_error_regression_routing")
        .select("*")
        .order("created_at", { ascending: true }),
      supabase
        .from("frontend_error_regression_notifications")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(50),
    ]);
    if (rErr) {
      toast({ title: "Could not load routing rules", description: rErr.message, variant: "destructive" });
    } else {
      setRules((r ?? []) as unknown as Rule[]);
    }
    setOutcomes((o ?? []) as unknown as Outcome[]);
    setLoading(false);
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const add = async () => {
    const destination = draft.destination.trim();
    if (!draft.label.trim() || !destination) {
      toast({ title: "Label and destination are required", variant: "destructive" });
      return;
    }
    if (draft.channel === "email" && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(destination)) {
      toast({ title: "Enter a valid email address", variant: "destructive" });
      return;
    }
    if (draft.channel === "slack" && !/^https:\/\/hooks\.slack\.com\//.test(destination)) {
      toast({ title: "Enter a Slack incoming webhook URL", variant: "destructive" });
      return;
    }
    setAdding(true);
    const { data: session } = await supabase.auth.getSession();
    const { error } = await supabase.from("frontend_error_regression_routing").insert({
      label: draft.label.trim(),
      release_pattern: draft.release_pattern.trim() || null,
      max_p_value: clamp(Number(draft.max_p_value), 0, 1, 0.05),
      min_rate_delta: clamp(Number(draft.min_rate_delta), 0, 1000, 0),
      min_release_errors: Math.max(1, Math.round(Number(draft.min_release_errors) || 1)),
      min_severity: draft.min_severity,
      channel: draft.channel,
      destination,
      escalate: draft.escalate,
      created_by: session.session?.user.id ?? null,
    });
    setAdding(false);
    if (error) {
      toast({ title: "Could not add rule", description: error.message, variant: "destructive" });
      return;
    }
    setDraft(EMPTY);
    toast({ title: "Routing rule added" });
    void load();
  };

  const patch = async (id: string, changes: Partial<Rule>) => {
    setBusyId(id);
    setRules((prev) => prev.map((r) => (r.id === id ? { ...r, ...changes } : r)));
    const { error } = await supabase
      .from("frontend_error_regression_routing")
      .update(changes as never)
      .eq("id", id);
    setBusyId(null);
    if (error) {
      toast({ title: "Update failed", description: error.message, variant: "destructive" });
      void load();
    }
  };

  const remove = async (id: string) => {
    setBusyId(id);
    const { error } = await supabase.from("frontend_error_regression_routing").delete().eq("id", id);
    setBusyId(null);
    if (error) {
      toast({ title: "Delete failed", description: error.message, variant: "destructive" });
      return;
    }
    setRules((prev) => prev.filter((r) => r.id !== id));
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Route className="size-4 text-primary" /> Regression notification routing
          </CardTitle>
          <CardDescription>
            Decide which crash-rate regressions are worth a ping: require statistical significance
            (p ≤ threshold), a minimum errors-per-session delta over the baseline release, and a
            minimum error volume. Every match — and every deliberate skip — is recorded below.
          </CardDescription>
        </div>
        <Button size="sm" variant="outline" onClick={() => void load()} disabled={loading}>
          <RefreshCw className={`size-3.5 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </CardHeader>
      <CardContent className="space-y-6">
        {!canConfigure && (
          <p className="rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
            You have read-only access to routing rules. Admin role required to change them.
          </p>
        )}

        {canConfigure && (
          <div className="grid gap-3 rounded-md border border-border p-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-1.5">
              <Label htmlFor="rr-label" className="text-xs">Rule name</Label>
              <Input
                id="rr-label"
                value={draft.label}
                placeholder="Significant spikes → #eng-oncall"
                onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rr-release" className="text-xs">Release prefix (blank = any)</Label>
              <Input
                id="rr-release"
                value={draft.release_pattern}
                placeholder="1.4."
                onChange={(e) => setDraft((d) => ({ ...d, release_pattern: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rr-p" className="text-xs">Max p-value</Label>
              <Input
                id="rr-p"
                type="number"
                step="0.001"
                min={0}
                max={1}
                value={draft.max_p_value}
                onChange={(e) => setDraft((d) => ({ ...d, max_p_value: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rr-delta" className="text-xs">Min rate delta (errors/session)</Label>
              <Input
                id="rr-delta"
                type="number"
                step="0.001"
                min={0}
                value={draft.min_rate_delta}
                onChange={(e) => setDraft((d) => ({ ...d, min_rate_delta: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rr-min-errors" className="text-xs">Min errors in window</Label>
              <Input
                id="rr-min-errors"
                type="number"
                min={1}
                value={draft.min_release_errors}
                onChange={(e) => setDraft((d) => ({ ...d, min_release_errors: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rr-sev" className="text-xs">Minimum severity</Label>
              <Select
                value={draft.min_severity}
                onValueChange={(v) => setDraft((d) => ({ ...d, min_severity: v as Rule["min_severity"] }))}
              >
                <SelectTrigger id="rr-sev">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="info">Info and above</SelectItem>
                  <SelectItem value="warn">Warn and above</SelectItem>
                  <SelectItem value="error">Errors only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rr-channel" className="text-xs">Channel</Label>
              <Select
                value={draft.channel}
                onValueChange={(v) => setDraft((d) => ({ ...d, channel: v as Rule["channel"] }))}
              >
                <SelectTrigger id="rr-channel">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="slack">Slack webhook</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rr-dest" className="text-xs">
                {draft.channel === "email" ? "Email address" : "Slack webhook URL"}
              </Label>
              <Input
                id="rr-dest"
                value={draft.destination}
                placeholder={
                  draft.channel === "email" ? "oncall@yourdomain.com" : "https://hooks.slack.com/services/..."
                }
                onChange={(e) => setDraft((d) => ({ ...d, destination: e.target.value }))}
              />
            </div>
            <div className="flex items-center gap-2 lg:col-span-2">
              <Switch
                id="rr-escalate"
                checked={draft.escalate}
                onCheckedChange={(v) => setDraft((d) => ({ ...d, escalate: v }))}
              />
              <Label htmlFor="rr-escalate" className="text-xs">
                Open an escalating incident on match
              </Label>
            </div>
            <div className="flex items-end lg:col-span-2">
              <Button onClick={() => void add()} disabled={adding}>
                <Plus className="size-4" /> {adding ? "Adding…" : "Add rule"}
              </Button>
            </div>
          </div>
        )}

        <div className="space-y-2">
          {rules.length === 0 && !loading && (
            <p className="text-sm text-muted-foreground">
              No routing rules yet — regressions only open triage incidents until you add one.
            </p>
          )}
          {rules.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center gap-2 rounded-md border border-border p-3 text-xs">
              <Badge variant={r.channel === "slack" ? "secondary" : "outline"}>{r.channel}</Badge>
              <span className="font-medium">{r.label}</span>
              <span className="font-mono break-all text-muted-foreground">{r.destination}</span>
              <span className="text-muted-foreground">
                p ≤ {r.max_p_value} · Δ ≥ {r.min_rate_delta} · ≥ {r.min_release_errors} errors ·{" "}
                {r.min_severity}+ · {r.release_pattern ? `release ${r.release_pattern}*` : "any release"}
              </span>
              {r.escalate && <Badge variant="outline">escalates</Badge>}
              <div className="ml-auto flex items-center gap-2">
                <Label htmlFor={`rr-on-${r.id}`} className="text-xs text-muted-foreground">Enabled</Label>
                <Switch
                  id={`rr-on-${r.id}`}
                  checked={r.enabled}
                  disabled={!canConfigure || busyId === r.id}
                  onCheckedChange={(v) => void patch(r.id, { enabled: v })}
                />
                {canConfigure && (
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={`Remove rule ${r.label}`}
                    disabled={busyId === r.id}
                    onClick={() => void remove(r.id)}
                  >
                    <Trash2 className="size-3.5 text-destructive" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="space-y-2">
          <h4 className="text-sm font-medium">Notification outcomes</h4>
          {outcomes.length === 0 ? (
            <p className="text-sm text-muted-foreground">No regression notifications recorded yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-border text-left uppercase text-muted-foreground">
                    <th className="py-2 pr-3 font-medium">When</th>
                    <th className="py-2 pr-3 font-medium">Release</th>
                    <th className="py-2 pr-3 font-medium">Stats</th>
                    <th className="py-2 pr-3 font-medium">Rule</th>
                    <th className="py-2 pr-3 font-medium">Destination</th>
                    <th className="py-2 font-medium">Outcome</th>
                  </tr>
                </thead>
                <tbody>
                  {outcomes.map((o) => (
                    <tr key={o.id} className="border-b border-border/50 last:border-0">
                      <td className="py-2 pr-3 text-muted-foreground">
                        {new Date(o.created_at).toLocaleString()}
                      </td>
                      <td className="py-2 pr-3 font-mono">
                        {o.release}
                        {o.baseline_release ? ` vs ${o.baseline_release}` : ""}
                      </td>
                      <td className="py-2 pr-3 font-mono text-muted-foreground">
                        z={o.z_score ?? "—"} · p={o.p_value ?? "—"} · Δ={o.rate_delta ?? "—"} ·{" "}
                        {o.release_errors ?? 0} errors
                      </td>
                      <td className="py-2 pr-3">{o.rule_label ?? "—"}</td>
                      <td className="py-2 pr-3 font-mono break-all text-muted-foreground">
                        {o.destination ?? "—"}
                      </td>
                      <td className="py-2">
                        <Badge
                          variant={
                            o.outcome === "delivered"
                              ? "secondary"
                              : o.outcome === "failed"
                                ? "destructive"
                                : "outline"
                          }
                        >
                          {o.outcome}
                        </Badge>
                        {o.skip_reason && (
                          <span className="ml-2 text-muted-foreground">{o.skip_reason}</span>
                        )}
                        {o.error && <span className="ml-2 text-destructive">{o.error}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function clamp(value: number, min: number, max: number, fallback: number) {
  if (!Number.isFinite(value)) return fallback;
  return Math.min(max, Math.max(min, value));
}

export default FrontendErrorRegressionRoutingPanel;
